import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import { toAppPath } from "../../lib/appNavigation";
import "./AppBreadcrumb.scss";

/**
 * Reusable breadcrumb for nested pages.
 *
 * @example
 * <AppBreadcrumb
 *   items={[
 *     { label: "Vendor", path: "/vendor" },
 *     { label: "Vendor SMSC" },
 *   ]}
 * />
 *
 * Each item:
 * - `label` (required) — display text
 * - `path` (optional) — in-app route; uses toAppPath() for obfuscated URLs
 * - `onClick` (optional) — custom handler instead of navigation
 *
 * The last item (or any item without path/onClick) renders as the current page.
 */
const AppBreadcrumb = ({
  items = [],
  className = "",
  showBackButton = false,
  backTo,
  /** `"slash"` → ` / ` (Users / Multi Access); default chevron icon */
  separator = "chevron",
}) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();

  if (!items.length) return null;

  const parentPath =
    backTo ??
    [...items]
      .slice(0, -1)
      .reverse()
      .find((item) => item.path)?.path;

  const handleBack = () => {
    if (parentPath) navigate(toAppPath(parentPath));
  };

  const renderSeparator = () =>
    separator === "slash" ? (
      <span className="appBreadcrumb__separator appBreadcrumb__separator--slash" aria-hidden="true">
        /
      </span>
    ) : (
      <FaChevronRight
        className="appBreadcrumb__separator"
        aria-hidden="true"
      />
    );

  return (
    <nav
      className={`appBreadcrumb ${className}`.trim()}
      aria-label={t("breadcrumb.ariaLabel", { defaultValue: "Breadcrumb" })}
    >
      {showBackButton && parentPath && (
        <button
          type="button"
          className="appBreadcrumb__backBtn userConf"
          onClick={handleBack}
        >
          <FaChevronLeft size={14} />
          <span>{t("back", { defaultValue: "Back" })}</span>
        </button>
      )}

      <ol className="appBreadcrumb__list">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          const isClickable = !isLast && (item.path || item.onClick);

          return (
            <li key={`${item.label}-${index}`} className="appBreadcrumb__item">
              {isClickable && item.path ? (
                <Link className="appBreadcrumb__link" to={toAppPath(item.path)}>
                  {item.label}
                </Link>
              ) : isClickable && item.onClick ? (
                <button
                  type="button"
                  className="appBreadcrumb__link appBreadcrumb__linkBtn"
                  onClick={item.onClick}
                >
                  {item.label}
                </button>
              ) : (
                <span
                  className="appBreadcrumb__current"
                  aria-current={isLast ? "page" : undefined}
                >
                  {item.label}
                </span>
              )}

              {!isLast && renderSeparator()}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default AppBreadcrumb;
